/**
 * Remove a member from a household. Mirrors iOS
 * `MemberDashboardView.swift:159-185` batch deletion.
 *
 * Touches two docs in a Firestore batch:
 *   1. `households/{householdId}.household_members` — `arrayRemove(memberId)`
 *   2. `member_profiles/{memberId}.households` — `arrayRemove(householdId)`
 *
 * For pets we additionally flip `isActive=false` so the doc is
 * preserved (their meals / vet notes / posts stay reachable from
 * activity surfaces) but they're hidden from rosters. Auth-linked
 * humans are *not* deleted; their posts/chats/responsibilities remain
 * intact, and they can rejoin via a new invite.
 */

import {
  arrayRemove,
  doc,
  getFirestore,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface RemoveMemberArgs {
  householdId: string;
  memberId: string;
  /** Pet membership flips `isActive=false` instead of unlinking. */
  isPet?: boolean;
}

export async function removeMemberFromHousehold({
  householdId,
  memberId,
  isPet,
}: RemoveMemberArgs): Promise<void> {
  // `getFirestore()` returns the same default app instance `db`
  // already wraps; passing `db` explicitly keeps it tied to whichever
  // app the rest of the client uses.
  void getFirestore;
  const batch = writeBatch(db);
  batch.update(doc(db, "households", householdId), {
    household_members: arrayRemove(memberId),
  });
  batch.update(
    doc(db, "member_profiles", memberId),
    isPet
      ? { isActive: false, households: arrayRemove(householdId) }
      : { households: arrayRemove(householdId) },
  );
  await batch.commit();
}
